import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UtilizationProductRow } from '../../../../../utils/reports/utilizationReport';
import { getCategoryTextColor } from '../../../../orderhistory/categoryUtils';
import { WasteDonutCard, WasteDonutItem } from './WasteDonutCard';

const SUPPLIER_PALETTE = ['#53B1FD', '#F38744', '#FF692E', '#FAC515', '#3DCB9A', '#717BBC', '#9E77ED', '#2E90FA', '#F04438'];

interface WasteBreakdownChartsProps {
  rows: UtilizationProductRow[];
}

/** Builds a {label,value,percentage,color} donut dataset from grouped positive waste €. */
function toDonutData(groups: Map<string, number>, color: (key: string, i: number) => string): WasteDonutItem[] {
  const entries = Array.from(groups.entries()).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total <= 0) return [];
  return entries.map(([label, value], i) => ({ label, value, percentage: (value / total) * 100, color: color(label, i) }));
}

/**
 * Two potential-waste donuts side by side — by category (left) and by supplier (right) —
 * reusing the all-restaurants "Spend by category" donut/legend handling via WasteDonutCard.
 */
export const WasteBreakdownCharts: React.FC<WasteBreakdownChartsProps> = ({ rows }) => {
  const { t } = useTranslation();

  const { byCategory, bySupplier } = useMemo(() => {
    const cat = new Map<string, number>();
    const sup = new Map<string, number>();
    for (const r of rows.flatMap(row => row.restaurantContributions ?? [row])) {
      if (r.wasteEur === null || r.wasteEur <= 0) continue;
      const c = r.category || t('reports.utilization.uncategorized');
      cat.set(c, (cat.get(c) ?? 0) + r.wasteEur);
      sup.set(r.supplier, (sup.get(r.supplier) ?? 0) + r.wasteEur);
    }
    return {
      byCategory: toDonutData(cat, (key) => getCategoryTextColor(key)),
      bySupplier: toDonutData(sup, (_key, i) => SUPPLIER_PALETTE[i % SUPPLIER_PALETTE.length]),
    };
  }, [rows, t]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      <WasteDonutCard title={t('reports.utilization.category.title')} data={byCategory} tooltipId="utilization-waste-by-category-tooltip" />
      <WasteDonutCard title={t('reports.utilization.supplierChart.title')} data={bySupplier} tooltipId="utilization-waste-by-supplier-tooltip" />
    </div>
  );
};

export default WasteBreakdownCharts;
